/*
  CARD STORIES — roaming recto/verso "peek" for the shop-all WALL (+ the mobile
  home cards). The page RESTS ON RECTO; a small ROAMING subset of tiles briefly
  peeks VERSO then returns to recto, so at any instant there are always FAR MORE
  recto than verso (Mattia), the movement is spread across the WHOLE wall (not
  just the first tiles), and it begins immediately.

  Invariant: at most `maxVerso` tiles are anywhere in the peek pipeline at once
  (≈ FRACTION of the VISIBLE tiles, hard-capped at CAP) — so verso can never be
  the majority. Visible-only via IntersectionObserver (memory-safe). A per-tile
  COOLDOWN after each peek pushes the active subset to roam to OTHER tiles
  instead of re-flipping the same few.

  DOM it drives (contract unchanged):
  - wall: .towel-card-wrap + lines [data-card-flip][data-face="recto|verso"]
          + faces .towel-card__image--recto / --verso
  - home: .front-card + lines [data-card-dot="recto|verso"] + faces --a / --b
  - showing the verso = attribute [data-card-flipped] on the card root
  - each face line is a 1px charging bar driven by --charge-pos / --charge-dur
*/
(function () {
  if (window.__sqmCardStories) return;
  window.__sqmCardStories = true;

  // FRACTION keeps recto the clear majority (with ~12 visible tiles that is ~2
  // showing verso at a time). CAP bounds memory on a long wall.
  var FRACTION = 0.2;
  var CAP = 8;
  var DUR_TO = 2000;   // verso bar charges (recto still shown) before the flip
  var HOLD = 1500;     // verso held
  var DUR_BACK = 1300; // recto bar charges (verso shown) before flipping back
  var COOLDOWN = 3500; // rest before a tile may peek again (forces roaming)

  var timers = new WeakMap(); // root -> active setTimeout id (pipeline OR cooldown)
  var visible = new Set();    // roots currently intersecting
  var peeking = new Set();    // roots currently in the peek pipeline (showing/turning verso)

  function dots(root) {
    var list = root.querySelectorAll('[data-card-dot], [data-card-flip]');
    var recto = null, verso = null;
    for (var i = 0; i < list.length; i++) {
      var f = list[i].getAttribute('data-card-dot') || list[i].getAttribute('data-face');
      if (f === 'recto') recto = list[i];
      else if (f === 'verso') verso = list[i];
    }
    return { recto: recto, verso: verso };
  }

  function hasVerso(root) {
    return !!(root.querySelector('.front-card__image--b') || root.querySelector('.towel-card__image--verso'));
  }

  function setFace(root, showVerso) {
    if (showVerso) root.setAttribute('data-card-flipped', '');
    else root.removeAttribute('data-card-flipped');
    var d = dots(root);
    if (d.recto) d.recto.setAttribute('aria-pressed', showVerso ? 'false' : 'true');
    if (d.verso) d.verso.setAttribute('aria-pressed', showVerso ? 'true' : 'false');
  }

  // bar states via custom props on the dot button (the ::before reads them)
  function arm(dot) { if (dot) { dot.style.setProperty('--charge-dur', '0s'); dot.style.setProperty('--charge-pos', '100%'); } }
  function full(dot) { if (dot) { dot.style.setProperty('--charge-dur', '0s'); dot.style.setProperty('--charge-pos', '0%'); } }
  function charge(dot, startFrac, ms) {
    if (!dot) return;
    dot.style.setProperty('--charge-dur', '0s');
    dot.style.setProperty('--charge-pos', ((1 - startFrac) * 100) + '%');
    void dot.offsetWidth; // reflow so the next change animates from there
    dot.style.setProperty('--charge-dur', Math.round(ms) + 'ms');
    dot.style.setProperty('--charge-pos', '0%'); // black sweeps in to full
  }

  function setTimer(root, fn, ms) { timers.set(root, setTimeout(fn, ms)); }
  function clearTimer(root) { var t = timers.get(root); if (t) { clearTimeout(t); timers.delete(root); } }
  function maxVerso() { return Math.max(1, Math.min(CAP, Math.round(visible.size * FRACTION))); }

  function rest(root) { var d = dots(root); setFace(root, false); full(d.recto); arm(d.verso); }

  // small ±25% jitter so peeks never run in lock-step
  function jit(ms) { return Math.round(ms * (0.75 + Math.random() * 0.5)); }

  // one full peek (durations jittered): recto (charge verso) -> verso (hold) ->
  // verso (charge recto) -> recto. Then a COOLDOWN keeps THIS tile ineligible
  // while a DIFFERENT tile immediately takes the freed slot — so the verso peeks
  // roam continuously and stay desynced instead of pulsing all at once. The
  // caller (fill) already reserved the slot in `peeking`.
  function peek(root) {
    var d = dots(root);
    setFace(root, false);
    full(d.recto); arm(d.verso);
    var dto = jit(DUR_TO), hold = jit(HOLD), dback = jit(DUR_BACK);
    charge(d.verso, 0, dto);
    setTimer(root, function () {
      setFace(root, true);
      full(d.verso); arm(d.recto);
      setTimer(root, function () {
        charge(d.recto, 0, dback);
        setTimer(root, function () {
          rest(root);
          peeking.delete(root);
          setTimer(root, function () { timers.delete(root); fill(); }, jit(COOLDOWN));
          fill(); // hand the freed slot to a different tile now (continuous roam)
        }, dback);
      }, hold);
    }, dto);
  }

  // keep up to maxVerso tiles in the pipeline. Picks RANDOM eligible tiles and
  // STAGGERS their starts so the peeks spread across the wall and never sync; a
  // slot is reserved in `peeking` up front so the budget holds during the
  // stagger delay. The first pick starts at once so something moves immediately.
  function fill() {
    var budget = maxVerso() - peeking.size;
    if (budget <= 0) return;
    var elig = [];
    visible.forEach(function (root) {
      if (!timers.has(root) && !peeking.has(root) && hasVerso(root)) elig.push(root);
    });
    for (var i = elig.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var t = elig[i]; elig[i] = elig[j]; elig[j] = t; }
    for (var k = 0; k < budget && k < elig.length; k++) {
      var root = elig[k];
      peeking.add(root); // reserve the slot during the stagger delay
      var delay = k === 0 ? 0 : Math.round(Math.random() * DUR_TO * 1.5);
      setTimer(root, (function (r) { return function () { peek(r); }; })(root), delay);
    }
  }

  function start(root) { visible.add(root); fill(); }
  function stop(root) {
    visible.delete(root);
    if (timers.has(root) || peeking.has(root)) {
      clearTimer(root);
      peeking.delete(root);
      rest(root);
      fill(); // a freed slot may let another visible tile peek
    }
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) start(e.target); else stop(e.target); });
      }, { rootMargin: '0px' })
    : null;

  function scan() {
    // Desktop drives the shop-all WALL only (the home keeps its hover crossfade);
    // mobile drives both the home cards and the wall. (Re-evaluated each scan().)
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var sel = mobile ? '.front-card, .towel-card-wrap' : '.towel-card-wrap';
    document.querySelectorAll(sel).forEach(function (root) {
      if (root.__sqmStories || !hasVerso(root)) return;
      root.__sqmStories = true;
      if (io) io.observe(root); else start(root);
    });
  }

  // manual tap on a face line: flip to the tapped face now. A verso tap
  // auto-returns to recto after a hold so the recto-majority invariant holds.
  // SINGLE owner of these taps (the sections' own handlers were removed to stop
  // a double-fire).
  document.addEventListener('click', function (e) {
    var line = e.target.closest('[data-card-dot], [data-card-flip]');
    if (!line) return;
    var root = line.closest('.front-card, .towel-card-wrap');
    if (!root) return;
    var face = line.getAttribute('data-card-dot') || line.getAttribute('data-face');
    clearTimer(root);
    peeking.delete(root);
    var d = dots(root);
    if (face === 'verso') {
      peeking.add(root);
      setFace(root, true);
      full(d.verso); arm(d.recto);
      setTimer(root, function () {
        charge(d.recto, 0, DUR_BACK);
        setTimer(root, function () {
          rest(root);
          peeking.delete(root);
          setTimer(root, function () { timers.delete(root); fill(); }, COOLDOWN);
        }, DUR_BACK);
      }, HOLD);
    } else {
      rest(root);
      fill();
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  window.addEventListener('load', scan); // catch lazily-added content
})();
